import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "./BarChart.css";

export default function BarChart({
  dataPoints = [],
  orientation = "horizontal",  
  width = 500,
  height = 600,
  fontSize = 9,
}) {
  const isStacked =
    Array.isArray(dataPoints) &&
    dataPoints.length > 0 &&
    dataPoints[0]?.type === "stackedColumn";

  const defaultPalette = {
    Positiva: "#0F9D58",
    Neutra: "#F4B400",
    Negativa: "#DB4437",
  };

  const { categories, series } = useMemo(() => {
    if (isStacked) {
      const catSet = new Set();
      dataPoints.forEach(s =>
        (s.dataPoints || []).forEach(p => catSet.add(String(p.label ?? "")))
      );
      const cats = Array.from(catSet);

      const seriesMapped = dataPoints.map(s => {
        const name = s.name ?? s.label ?? "";
        const color = s.color ?? defaultPalette[name] ?? undefined;
        const values = cats.map(cLabel => {
          const found = (s.dataPoints || []).find(p => String(p.label ?? "") === cLabel);
          return found?.y ?? 0;
        });
        return { name, data: values, color };
      });

      return { categories: cats, series: seriesMapped };
    } else {
      const arr = dataPoints || [];
      const sorted = [...arr].sort(
        (a, b) => (b?.y ?? b?.value ?? 0) - (a?.y ?? a?.value ?? 0)
      );

      const cats = sorted.map(d => String(d.label ?? d.name ?? ""));
      const data = sorted.map(d => ({
        y: d?.y ?? d?.value ?? 0,
        color: d?.color,
      }));

      return { categories: cats, series: [{ name: "Total", data }] };
    }
  }, [dataPoints, isStacked, orientation]);

  const chartType = orientation === "vertical" ? "column" : "bar";

  const options = {
    chart: {
      type: chartType,
      backgroundColor: "transparent",
      width,
      height,
      spacing: [10, 10, 10, 10],
      marginTop: 70,
      animation: false,
    },
    title: { text: null },
    credits: { enabled: false },
    tooltip: { enabled: false },

    legend: { enabled: isStacked }, 
    xAxis: {
      categories,
      labels: {
        useHTML: true,
        style: { fontSize: `${fontSize}px`, color: "#444", whiteSpace: "nowrap" },
        rotation: orientation === "vertical" ? -30 : 0,
      },
      lineWidth: 0,
      tickWidth: 0,
    },
    yAxis: {
      title: { text: null },
      labels: { style: { fontSize: `${fontSize}px`, color: "#666" } },
      gridLineColor: "#e0e0e0",
      min: 0,
      stackLabels: {
        enabled: isStacked,
        style: { fontSize: `${fontSize}px`, fontWeight: "bold", color: "#333" },
      },
    },

    plotOptions: {
      series: {
        borderWidth: 0,
        stacking: isStacked ? "normal" : undefined,
        dataLabels: {
          enabled: true,
          style: { fontSize: `${fontSize}px`, color: "#111", textOutline: "none" },
          formatter: function () {
            if (!this.y || this.y <= 0) return "";

            const val = this.y;
            const str = Math.abs(val) >= 10000
              ? Highcharts.numberFormat(val, Number.isInteger(val) ? 0 : 2, ",", ".")
              : (Number.isInteger(val) ? val.toString() : val.toFixed(2));

            return str.replace(/\.00$/, "");
          },
        },
      },
      bar: {
        pointPadding: 0.12,
        groupPadding: 0.12,
        borderRadius: 3,
      },
      column: {
        pointPadding: 0.08,
        groupPadding: 0.08,
        borderRadius: 3,
      },
    },

    series,
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}
